import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export function usePinDetails(pinId: string | undefined) {
  const [pin, setPin] = useState<any>(null);
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [comments, setComments] = useState<any[]>([]);
  const [relatedPins, setRelatedPins] = useState<any[]>([]);
  const [likeCount, setLikeCount] = useState(0);
  const [userHasLiked, setUserHasLiked] = useState(false);
  const [commentCount, setCommentCount] = useState(0);
  const [collections, setCollections] = useState<any[]>([]);

  useEffect(() => {
    console.log("usePinDetails hook called with id:", pinId);
    
    if (!pinId) {
      setLoadError("ID do pin não fornecido");
      setLoading(false);
      return;
    }
    
    // Fetch data on mount
    fetchPin();
    fetchCurrentUser();
  }, [pinId]);

  const fetchPin = async () => {
    if (!pinId) return;

    try {
      setLoading(true);
      setLoadError(null);
      
      console.log("Fetching pin data for ID:", pinId);
      const { data: pinData, error: pinError } = await supabase
        .from("posts")
        .select("*, profile:profiles(username, avatar_url)")
        .eq("id", pinId)
        .maybeSingle();

      if (pinError) {
        console.error("Error fetching pin:", pinError);
        throw pinError;
      }
      
      if (!pinData) {
        console.log("No pin data found for ID:", pinId);
        setLoadError("Pin não encontrado");
        setLoading(false);
        return;
      }
      
      console.log("Pin data received:", pinData);
      setPin(pinData);
      
      await fetchComments();
      await fetchLikes();
      
      if (pinData.category) {
        console.log("Fetching related pins for category:", pinData.category);
        const { data: relatedData } = await supabase
          .from("posts")
          .select("*")
          .eq("category", pinData.category)
          .neq("id", pinId)
          .limit(6);
        
        setRelatedPins(relatedData || []);
      }
    } catch (error) {
      console.error("Error fetching pin:", error);
      setLoadError("Erro ao carregar o pin");
      toast.error("Erro ao carregar o pin");
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const { data } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", session.user.id)
          .maybeSingle();
        
        if (data) {
          setUser({ ...data, id: session.user.id });
          
          await fetchUserCollections(session.user.id);
        }
      }
    } catch (error) {
      console.error("Error fetching user:", error);
    }
  };

  const fetchUserCollections = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from("collections")
        .select(`
          id, 
          title, 
          cover_image_url,
          collection_items (
            post_id
          )
        `)
        .eq("user_id", userId);
      
      if (error) throw error;
      
      const transformedCollections = data?.map(collection => ({
        ...collection,
        pins: collection.collection_items?.map((item: any) => ({ id: item.post_id })) || []
      })) || [];
      
      setCollections(transformedCollections);
    } catch (error) {
      console.error("Error fetching collections:", error);
    }
  };

  const fetchComments = async () => {
    if (!pinId) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;

      const { data: commentsData, error } = await supabase
        .from("pin_comments")
        .select(`
          *,
          profile:profiles(username, avatar_url)
        `)
        .eq("pin_id", pinId)
        .order("created_at", { ascending: false });

      if (error) throw error;

      let commentsWithLikes = commentsData || [];
      
      if (commentsWithLikes.length > 0) {
        const commentIds = commentsWithLikes.map((c: any) => c.id);
        
        const { data: likesCountData, error: countError } = await supabase.rpc(
          'count_comment_likes',
          { comment_ids: commentIds }
        );
        
        if (countError) {
          console.error("Error counting likes:", countError);
          commentsWithLikes = commentsWithLikes.map(comment => ({
            ...comment,
            likes_count: 0,
            user_has_liked: false
          }));
        } else {
          const likesCountMap = new Map();
          if (likesCountData) {
            likesCountData.forEach((item: any) => {
              likesCountMap.set(item.comment_id, parseInt(item.count));
            });
          }
          
          let userLikedCommentIds = new Set();
          if (userId) {
            const { data: userLikes, error: likesError } = await supabase
              .from("pin_comment_likes")
              .select("comment_id")
              .eq("user_id", userId)
              .in("comment_id", commentIds);
            
            if (!likesError && userLikes) {
              userLikedCommentIds = new Set(userLikes.map((like: any) => like.comment_id));
            }
          }
          
          commentsWithLikes = commentsWithLikes.map((comment: any) => ({
            ...comment,
            likes_count: likesCountMap.get(comment.id) || 0,
            user_has_liked: userLikedCommentIds.has(comment.id)
          }));
        }
      }

      setComments(commentsWithLikes);
      setCommentCount(commentsWithLikes.length);
    } catch (error) {
      console.error("Error fetching comments:", error);
      toast.error("Erro ao carregar comentários");
    }
  };

  const fetchLikes = async () => {
    if (!pinId) return;

    try {
      const { count, error } = await supabase
        .from("pin_likes")
        .select("*", { count: "exact", head: true })
        .eq("pin_id", pinId);

      if (error) throw error;
      setLikeCount(count || 0);

      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const { data, error: likesError } = await supabase
          .from("pin_likes")
          .select("*")
          .eq("pin_id", pinId)
          .eq("user_id", session.user.id)
          .maybeSingle();

        if (likesError) throw likesError;
        setUserHasLiked(!!data);
      }
    } catch (error) {
      console.error("Error fetching likes:", error);
    }
  };

  const toggleLike = async () => {
    if (!user) {
      toast.error("Você precisa estar logado para curtir");
      return;
    }

    try {
      if (userHasLiked) {
        const { error } = await supabase
          .from("pin_likes")
          .delete()
          .eq("pin_id", pinId)
          .eq("user_id", user.id);

        if (error) throw error;
      } else {
        const { error } = await supabase.from("pin_likes").insert({
          pin_id: pinId,
          user_id: user.id,
        });

        if (error) throw error;
      }

      await fetchLikes();
    } catch (error) {
      console.error("Error toggling like:", error);
      toast.error("Erro ao curtir o pin");
    }
  };

  const saveToCollection = async (collectionId: string) => {
    if (!user) {
      toast.error("Você precisa estar logado para salvar pins");
      return;
    }

    try {
      const { data: existingItem } = await supabase
        .from("collection_items")
        .select("*")
        .eq("collection_id", collectionId)
        .eq("post_id", pinId)
        .maybeSingle();

      if (existingItem) {
        toast.info("Este pin já está salvo nesta coleção");
        return;
      }

      // Get the image URL from the pin to potentially set as cover image
      const pinImageUrl = pin?.image_url;

      // Get the current collection to check if it needs a cover image
      const { data: collection } = await supabase
        .from("collections")
        .select("cover_image_url")
        .eq("id", collectionId)
        .single();

      // If no cover image exists, update the collection with this pin's image
      if (collection && !collection.cover_image_url && pinImageUrl) {
        await supabase
          .from("collections")
          .update({ cover_image_url: pinImageUrl })
          .eq("id", collectionId);
      }

      // Insert the pin into the collection
      const { error } = await supabase.from("collection_items").insert({
        collection_id: collectionId,
        post_id: pinId,
      });

      if (error) throw error;

      await fetchUserCollections(user.id);
      toast.success("Pin salvo na coleção com sucesso");
    } catch (error) {
      console.error("Error saving to collection:", error);
      toast.error("Erro ao salvar na coleção");
    }
  };

  const handleCommentAdded = () => {
    fetchComments();
  };

  return {
    pin,
    user,
    loading,
    loadError,
    comments,
    relatedPins,
    likeCount,
    userHasLiked,
    commentCount,
    collections,
    toggleLike,
    saveToCollection,
    handleCommentAdded,
    fetchComments
  };
}
